/**
 * Filter Interaction Tracking Service
 *
 * Tracks user interactions with SRP property filters via API calls.
 * Captures: filter selections, deselections, and clears with full context
 * including user info, session data, and traffic attribution.
 *
 * API Endpoint: /portal/srp/filter-interaction
 */

import { baseApi } from "@/api/apiConfig";
import { store } from "@/store/store";
import { buildUtmInfo } from "./attributionCapture";
import Cookies from "js-cookie";

// Deduplication: track last event to prevent duplicates on re-renders
let lastEventKey = "";
let lastEventTime = 0;
const DEDUP_WINDOW_MS = 500;

// SRP route keywords that are NOT location segments (used to distinguish
// a city path segment from a category/listing-type route segment).
const SRP_ROUTE_KEYWORDS = [
  "auctions",
  "bank-owned",
  "foreclosures",
  "short-sale",
  "non-bank-owned",
  "commercial",
  "near-me",
  "near-by",
  "traditional-sale",
];

/**
 * Get the current SRP location for use in GA4 `eventLabel`.
 *
 * Derives the real location the user is searching, using the SAME logic as the
 * filter-interaction API: prefer selected states in Redux, otherwise fall back
 * to the URL path (state / state+city / zip). Returns "All States" only when no
 * location filter is actually applied.
 *
 * Examples: "FL", "FL/miami", "Zip: 33101", "FL,TX", "All States".
 *
 * @returns {string} Human-readable location label.
 */
export function getSrpLocationLabel() {
  try {
    const properties = store.getState().properties || {};

    // 1) Prefer explicitly selected states from Redux
    if (properties.state?.length > 0) {
      return properties.state
        .map((s) => s.label || s.value)
        .filter(Boolean)
        .join(",");
    }

    // 2) Fall back to the URL path (state / state+city / zip)
    const pathSegments = window.location.pathname
      .toLowerCase()
      .split("/")
      .filter(Boolean);

    if (pathSegments.length >= 1) {
      const first = pathSegments[0];
      if (first.length === 2 && /^[a-z]+$/.test(first)) {
        const stateCode = first.toUpperCase();
        if (
          pathSegments.length >= 2 &&
          !SRP_ROUTE_KEYWORDS.includes(pathSegments[1])
        ) {
          return `${stateCode}/${pathSegments[1]}`;
        }
        return stateCode;
      }
      if (/^\d{5}$/.test(first)) {
        return `Zip: ${first}`;
      }
    }
  } catch {
    // fall through to default
  }

  // 3) No location filter applied
  return "All States";
}

/**
 * Build the filterType string from current Redux state (all active filters).
 * Format: "propertySubType=CO,propertySubType=MF,category=bankowned,..."
 */
function buildFilterString(properties) {
  const parts = [];
  const path = window.location.pathname.toLowerCase();
  const pathSegments = path.split("/").filter(Boolean);

  // Array-based filters
  if (properties.propertySubType?.length > 0) {
    properties.propertySubType.forEach((v) =>
      parts.push(`propertySubType=${v}`),
    );
  }
  if (properties.category?.length > 0) {
    properties.category.forEach((v) => parts.push(`category=${v}`));
  } else {
    // Derive category from current URL pathname if not in Redux state
    if (path.includes("/bank-owned")) parts.push("category=bankowned");
    else if (path.includes("/foreclosures")) parts.push("category=foreclosure");
    else if (path.includes("/short-sale")) parts.push("category=SHRT_SALE");
    else if (path.includes("/non-bank-owned"))
      parts.push("category=nonbankowned");
    else if (path === "/commercial" || path.endsWith("/commercial"))
      parts.push("category=commercial");
  }
  if (properties.propertyStatus?.length > 0) {
    properties.propertyStatus.forEach((v) => parts.push(`propertyStatus=${v}`));
  }
  if (properties.occupancyStatus?.length > 0) {
    properties.occupancyStatus.forEach((v) =>
      parts.push(`occupancyStatus=${v}`),
    );
  }
  if (properties.sid?.length > 0) {
    properties.sid.forEach((v) => parts.push(`sid=${v}`));
  }
  if (properties.propertyShowMoreStatus?.length > 0) {
    properties.propertyShowMoreStatus.forEach((v) =>
      parts.push(`propertyShowMoreStatus=${v}`),
    );
  }
  if (properties.state?.length > 0) {
    properties.state.forEach((v) => parts.push(`state=${v.value}`));
  } else {
    // Derive state/zip/city from URL path if not in Redux
    const srpRouteKeywords = ['auctions', 'bank-owned', 'foreclosures', 'short-sale', 'non-bank-owned', 'commercial', 'near-me', 'near-by', 'traditional-sale'];
    if (pathSegments.length >= 2) {
      const first = pathSegments[0];
      if (first.length === 2 && /^[a-z]+$/.test(first)) {
        parts.push(`state=${first.toUpperCase()}`);
        if (pathSegments.length >= 3 && !srpRouteKeywords.includes(pathSegments[1])) {
          parts.push(`city=${pathSegments[1]}`);
        }
      } else if (/^\d{5}$/.test(first)) {
        parts.push(`zip=${first}`);
      }
    }
  }

  // String-based filters
  if (properties.newToHubzu) {
    parts.push(`newToHubzu=${properties.newToHubzu}`);
  }
  if (properties.marketingPhase) {
    parts.push(`marketingPhase=${properties.marketingPhase}`);
  }
  if (properties.auctionEndingToday) {
    parts.push(`auctionEndingToday=${properties.auctionEndingToday}`);
  }
  if (properties.ownItNow) {
    parts.push(`ownItNow=${properties.ownItNow}`);
  }
  if (properties.nobuyerpremium) {
    parts.push(`nobuyerpremium=${properties.nobuyerpremium}`);
  }
  if (properties.brokerCoOp) {
    parts.push(`brokerCoOp=${properties.brokerCoOp}`);
  }
  if (properties.cashOnly) {
    parts.push(`cashOnly=${properties.cashOnly}`);
  }
  if (properties.listingType) {
    parts.push(`listingType=${properties.listingType}`);
  } else {
    // Derive listingType from URL path if not in Redux (e.g., /ca/auctions → listingType=AUCN)
    if (path.includes("/auctions")) {
      parts.push("listingType=AUCN");
    } else if (path.includes("/traditional-sale")) {
      parts.push("listingType=TRNL");
    }
  }

  // Sort
  if (properties.sort?.value) {
    parts.push(properties.sort.value);
  }

  // Range filters
  if (properties.price?.min > 0 || properties.price?.max > 0) {
    const min = properties.price.min || 0;
    const max =
      properties.price.max === 1000001
        ? "1000000plus"
        : properties.price.max || 0;
    parts.push(`priceRange=${min}-${max}`);
  }
  if (properties.area?.min || properties.area?.max) {
    const min = properties.area.min || 0;
    const max =
      properties.area.max === 10001 ? "10000plus" : properties.area.max || 0;
    parts.push(`squareFeetRange=${min}-${max}`);
  }
  if (properties.builtYear?.min > 0 || properties.builtYear?.max > 0) {
    const min =
      properties.builtYear.min === 1799
        ? "Before 1800"
        : properties.builtYear.min || 0;
    const max =
      properties.builtYear.max === 2011
        ? "After 2010"
        : properties.builtYear.max || 0;
    parts.push(`yearBuiltRange=${min}-${max}`);
  }
  if (properties.bedrooms) {
    parts.push(`bed=${properties.bedrooms}`);
  }
  if (properties.bathrooms) {
    parts.push(`bath=${properties.bathrooms}`);
  }
  if (properties.pageNumber && properties.pageNumber > 1) {
    parts.push(`pageNumber=${properties.pageNumber}`);
  }

  return parts.join(",");
}

/**
 * Track a filter interaction event via API.
 *
 * @param {string} eventType - "FILTER_SELECTED" | "FILTER_DESELECTED"
 * @param {object} [options] - Optional overrides
 * @param {string} [options.propertyId] - Property ID if in PDP context
 * @param {string} [options.deselectedFilters] - For FILTER_DESELECTED: comma-separated string of deselected filter values
 *   e.g., "propertySubType=CO" for single deselect, or "category=bankowned,category=foreclosure" for clear-all
 */
export function trackFilterInteraction(eventType, options = {}) {
  // Use setTimeout to ensure Redux state is updated before reading
  setTimeout(() => {
    try {
      const state = store.getState();
      const properties = state.properties;
      const user = state.user;

      // For FILTER_DESELECTED: use the deselected values passed in options
      // For FILTER_SELECTED: use the current active filters from Redux state
      let filterType;
      if (eventType === "FILTER_DESELECTED" && options.deselectedFilters) {
        filterType = options.deselectedFilters;
      } else {
        filterType = buildFilterString(properties);
      }

      // Deduplication check
      const eventKey = `${eventType}_${filterType}`;
      const now = Date.now();
      if (eventKey === lastEventKey && now - lastEventTime < DEDUP_WINDOW_MS) {
        return; // Skip duplicate event
      }
      lastEventKey = eventKey;
      lastEventTime = now;

      // Build payload matching the API contract
      const payload = {
        filterType: filterType,
        eventType: eventType,
        propertyId: options.propertyId || null,
        userType: user.isUserLoggedIn ? "LOGGED_IN" : "GUEST",
        visitorId: Cookies.get("visitorId"),
        userId: user.user?.realbidUserId || null,
        sourcePage: "SRP",
        entrySource: null,
        utmInfo: buildUtmInfo(),
      };

      // Fire-and-forget API call
      baseApi.post("/portal/srp/filter-interaction", payload).catch((err) => {
        console.error("[FilterTracking] API call failed:", err?.message || err);
      });
    } catch (err) {
      console.error("[FilterTracking] Error building payload:", err);
    }
  }, 0);
}

/**
 * Build filterType string from the current page URL.
 * Extracts filters from the URL path and query params.
 * Used for PAGE_VIEW tracking on SRP mount.
 *
 * @returns {string} Comma-separated filter values (e.g., "state=FL,category=commercial,sort=MostPopular")
 */
export function buildFilterTypeFromCurrentUrl() {
  const parts = [];
  const path = window.location.pathname.toLowerCase();
  const queryPart = window.location.search;

  // Extract state, zip, and city from path
  const pathSegments = path.split("/").filter(Boolean);
  const srpRouteKeywords = ['auctions', 'bank-owned', 'foreclosures', 'short-sale', 'non-bank-owned', 'commercial', 'near-me', 'near-by', 'traditional-sale'];

  if (pathSegments.length >= 2) {
    const first = pathSegments[0];
    if (first.length === 2 && /^[a-z]+$/.test(first)) {
      // First segment is a state code (2 letters)
      parts.push(`state=${first.toUpperCase()}`);
      // Check if second segment is a city (not a route keyword)
      if (pathSegments.length >= 3 && !srpRouteKeywords.includes(pathSegments[1])) {
        parts.push(`city=${pathSegments[1]}`);
      }
    } else if (/^\d{5}$/.test(first)) {
      // First segment is a zip code (5 digits)
      parts.push(`zip=${first}`);
    }
  }

  // Derive category from path
  if (path.includes("/bank-owned")) {
    parts.push("category=bankowned");
  } else if (path.includes("/foreclosures")) {
    parts.push("category=foreclosure");
  } else if (path.includes("/short-sale")) {
    parts.push("category=SHRT_SALE");
  } else if (path.includes("/non-bank-owned")) {
    parts.push("category=nonbankowned");
  } else if (path === "/commercial" || path.endsWith("/commercial")) {
    parts.push("category=commercial");
  }

  // Derive listingType from path
  if (path.includes("/auctions")) {
    parts.push("listingType=AUCN");
  } else if (path.includes("/traditional-sale")) {
    parts.push("listingType=TRNL");
  }

  // Parse query params
  if (queryPart) {
    const params = new URLSearchParams(queryPart);
    params.forEach((value, key) => {
      // Skip non-filter params and tracking params
      if (
        [
          "rect",
          "zoomValue",
          "swLatLng",
          "neLatLng",
          "centerPoint",
          "blp",
          "utm_source",
          "utm_medium",
          "utm_campaign",
          "utm_content",
          "utm_term",
          "utm_id",
          "gclid",
          "gclsrc",
          "gad_source",
          "gad_campaignid",
          "fbclid",
          "msclkid",
          "sfmc_id",
        ].includes(key)
      )
        return;
      // Skip if already derived from path
      if (
        key === "listingType" &&
        parts.some((p) => p.startsWith("listingType="))
      )
        return;
      parts.push(`${key}=${value}`);
    });
  }

  // Always include default sort if not already present
  if (!parts.some((p) => p.startsWith("sort="))) {
    parts.push("sort=MostPopular");
  }

  return parts.join(",");
}
